import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TicketDocument } from './console/TicketDocument';
import type { Ticket } from '@/lib/mock';
import { useTeamFields } from '@/lib/customFields';
import { TEAMS } from '@/lib/teams';

export function GlobalTicketSlideover({
  ticketId,
  tickets,
  now,
  onClose,
  actions,
}: {
  ticketId: string | null;
  tickets: Ticket[];
  now: number;
  onClose: () => void;
  actions: any; // TicketActions
}) {
  const [openId, setOpenId] = useState<string | null>(ticketId);

  useEffect(() => {
    setOpenId(ticketId);
  }, [ticketId]);

  const openTicket = tickets.find((t) => t.id === openId) ?? null;
  const owner = openTicket ? TEAMS.find((tm) => tm.name === openTicket.team || tm.match.includes(openTicket.team)) : null;
  
  // Always call hook unconditionally
  const { fields: customFields } = useTeamFields(owner?.id || '');

  if (!openTicket) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setOpenId(null); onClose(); }} />
      <div className={cn('absolute right-0 top-0 h-full w-full overflow-y-auto border-l border-[var(--color-neo-border)] bg-theme shadow-2xl')}>
        <button
          onClick={() => { setOpenId(null); onClose(); }}
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-[1rem] neo-interactive bg-[var(--color-surface)] text-muted-foreground transition hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <TicketDocument
          ticket={openTicket}
          now={now}
          allTickets={tickets}
          customFields={customFields}
          actions={{
            onStatus: (s: string) => actions.onStatus(openTicket.id, s),
            onAssign: (a: string | null, name?: string) => actions.onAssign(openTicket.id, a, name),
            onUpdate: (b: string, internal: boolean) => actions.onUpdate(openTicket.id, b, internal),
            onRelate: (target: string) => actions.onRelate(openTicket.id, target),
            onMerge: (into: string) => actions.onMerge(openTicket.id, into),
            onAddFile: (f: File) => actions.onAddFile(openTicket.id, f),
            onRemoveAttachment: (attId: string) => actions.onRemoveAttachment(openTicket.id, attId),
            onResolve: (s: string, c: string, n: string) => actions.onResolve(openTicket.id, s, c, n),
            onSetParent: (p: string | null) => actions.onSetParent(openTicket.id, p),
            onRequestApproval: (a: string) => actions.onRequestApproval(openTicket.id, a),
            onDecideApproval: (aid: string, dec: 'approved'|'rejected', note?: string) => actions.onDecideApproval(openTicket.id, aid, dec, note),
            onUpdateField: (field: string, value: any) => actions.onUpdateField(openTicket.id, field, value),
          }}
        />
      </div>
    </div>
  );
}
